/** Collision resolvers: ball-ball (impulse + throw) and ball-cushion (with English). */
import type { PhysicsConstants } from '../config';
import type { Vec3 } from '../types';
import type { Ball } from './model';
import { addScaled, cross, dot, scale, sub } from './vec';

/**
 * Resolve a ball-ball collision (assumes the pair currently overlaps). Returns
 * the impact speed (the normal closing velocity, m/s) if an impulse was applied,
 * or 0 if the pair was separating / not in contact — callers can treat the value
 * as a truthy "did it hit" flag and also use it to scale the collision sound.
 * Models the near-elastic normal impulse (the 90-degree rule) plus tangential
 * "throw" from relative surface velocity.
 */
export function resolveBallBall(a: Ball<string>, b: Ball<string>, c: PhysicsConstants): number {
  const d = sub(b.pos, a.pos);
  const dist = Math.hypot(d.x, d.y, d.z);
  if (dist < 1e-9) return 0;
  const n: Vec3 = scale(d, 1 / dist);

  const vRel = sub(a.vel, b.vel);
  const vn = dot(vRel, n);
  if (vn <= 0) return 0; // separating or parallel

  // --- normal impulse with restitution ---
  const e = c.ballBallRestitution;
  const jn = ((1 + e) * vn) / (a.invMass + b.invMass);
  a.vel = addScaled(a.vel, n, -jn * a.invMass);
  b.vel = addScaled(b.vel, n, jn * b.invMass);

  // --- tangential throw from relative surface velocity ---
  const surf = add3(vRel, cross(a.angVel, scale(n, a.radius)), cross(b.angVel, scale(n, b.radius)));
  const sn = dot(surf, n);
  const ut: Vec3 = { x: surf.x - sn * n.x, y: surf.y - sn * n.y, z: surf.z - sn * n.z };
  const utMag = Math.hypot(ut.x, ut.y, ut.z);
  if (utMag > 1e-9) {
    const uth = scale(ut, 1 / utMag);
    const mu = c.ballBallFriction.a + c.ballBallFriction.b * Math.exp(-c.ballBallFriction.c * utMag);
    const jt = Math.min(mu * jn, utMag / (a.invMass + b.invMass));
    const Jt = scale(uth, -jt); // friction on `a` opposes its relative surface motion
    a.vel = addScaled(a.vel, Jt, a.invMass);
    b.vel = addScaled(b.vel, Jt, -b.invMass);
    a.angVel = addScaled(a.angVel, cross(scale(n, a.radius), Jt), a.invInertia);
    b.angVel = addScaled(b.angVel, cross(scale(n, b.radius), Jt), b.invInertia);
  }
  return vn;
}

function add3(a: Vec3, b: Vec3, c: Vec3): Vec3 {
  return { x: a.x + b.x + c.x, y: a.y + b.y + c.y, z: a.z + b.z + c.z };
}

/**
 * Resolve a ball-cushion collision. `n` is the inward unit normal (pointing into
 * the table), `t` an in-plane unit tangent along the rail. Reflects the normal
 * component with restitution and applies rail friction coupled to side spin, so
 * running/reverse English changes the rebound — the key carom effect. Returns the
 * impact speed (inward closing velocity, m/s) if it rebounded, else 0.
 */
export function resolveCushion(b: Ball<string>, n: Vec3, t: Vec3, c: PhysicsConstants): number {
  const vn = dot(b.vel, n);
  if (vn >= 0) return 0; // not moving into the rail

  const R = b.radius;
  const vt = dot(b.vel, t);
  // tangential surface velocity contributed by spin at the contact point (-R n):
  const surfSpin = dot(cross(b.angVel, scale(n, -R)), t);
  const sT = vt + surfSpin;

  // restitution drops with approach speed — rails play "deader" when hit hard
  const e = Math.max(c.cushionRestitutionMin, c.cushionRestitution - c.cushionRestitutionSpeedFalloff * Math.abs(vn));
  const newVn = -e * vn;
  const normalImpulseSpeed = (1 + e) * Math.abs(vn);
  const towardRoll = (2 / 7) * sT;
  const slip = Math.sign(sT) * Math.min(c.cushionFriction * normalImpulseSpeed, Math.abs(towardRoll));
  const newVt = vt - slip;

  b.vel.x = n.x * newVn + t.x * newVt;
  b.vel.y = n.y * newVn + t.y * newVt;
  // vertical velocity (a hop) is preserved; grounded balls have vz = 0 anyway

  // tangential friction impulse Jt (along t) also torques the ball -> side spin change
  const Jt = b.mass * (newVt - vt);
  const torque = cross(scale(n, -R), scale(t, Jt));
  b.angVel = addScaled(b.angVel, torque, b.invInertia);

  // Cushion roll-grip: a rolling ball's topspin is about the rail-tangent axis and
  // still points INTO the rail after the bounce, so the ball skids back out. Grip
  // reorients that into-rail rolling spin toward the rebound velocity so the ball
  // rolls off instead. m = k x n is the horizontal axis it rolls about for
  // into-rail motion; nudge the spin about m from its current value toward the
  // rolling target (newVn / R). Leaves side English (z spin) untouched.
  const g = c.cushionRollGrip;
  if (g > 0) {
    const mx = -n.y;
    const my = n.x;
    const wm = b.angVel.x * mx + b.angVel.y * my; // current spin about m
    const dw = g * (newVn / R - wm); // toward the spin that rolls along the rebound
    b.angVel.x += dw * mx;
    b.angVel.y += dw * my;
  }
  return -vn; // inward closing speed (vn < 0 here), as a positive impact speed
}
